import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import {
  Avatar,
  Button,
  CardContent,
  FormControl,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
  IconButton,
  Box,
  Divider,
  Tooltip,
  Zoom,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  styled
} from '@mui/material';
import { CameraAlt, Edit, Check, Close, Link, Public, Instagram, LinkedIn } from '@mui/icons-material';
import { keyframes } from '@emotion/react';
import Container from '../../../components/layout/Container';
import Fieldset from '../../../components/Form/Fieldset';

// Componente DarkFieldset estilizado
const DarkFieldset = styled(Fieldset)`
  background-color: #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid #c0c0c0;
  transition: all 0.3s ease;
  background: linear-gradient(to bottom, #e8e8e8 0%, #d8d8d8 100%);

  &:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
    background: linear-gradient(to bottom, #e0e0e0 0%, #d0d0d0 100%);
  }
`;

// Animação personalizada
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Tema personalizado
const profileTheme = createTheme({
  palette: {
    primary: {
      main: '#2a3a8f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6b6b',
    },
    background: {
      default: '#f8f9fa',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 700,
    },
    body1: {
      lineHeight: 1.6,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          textTransform: 'none',
          padding: '12px 24px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
            },
          },
        },
      },
    },
  },
});

// Componentes estilizados
const FullHeightContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 'calc(100vh - 64px)',
  padding: '24px 0',
});

const ProfileHeader = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '80px 20px 40px',
  background: 'linear-gradient(135deg, #151c48 0%, #2a3a8f 100%)',
  color: 'white',
  position: 'relative',
  textAlign: 'center',
});

const AvatarContainer = styled('div')({
  position: 'absolute',
  top: '-40px',
  left: '50%',
  transform: 'translateX(-50%)',
  cursor: 'pointer',
  zIndex: 2,
  '&:hover': {
    '& $StyledAvatar': {
      animation: `${pulse} 1.5s infinite`,
    },
    '&::after': {
      content: '"Alterar foto"',
      position: 'absolute',
      bottom: '-28px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.9)',
      background: 'rgba(0, 0, 0, 0.7)',
      padding: '4px 12px',
      borderRadius: '12px',
      whiteSpace: 'nowrap',
    },
  },
});

const StyledAvatar = styled(Avatar)({
  width: '140px !important',
  height: '140px !important',
  border: '4px solid white',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
  backgroundColor: '#2a3a8f',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.03)',
  },
});

const CameraButton = styled('div')({
  position: 'absolute',
  bottom: '8px',
  right: '8px',
  background: '#151c48',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '3px solid white',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: '#2a3a8f',
    transform: 'scale(1.1)',
  },
});

const ProfileContent = styled(CardContent)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: '24px',
});

const SectionHeader = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
});

const SectionTitle = styled(Typography)({
  fontWeight: '700 !important',
  color: '#151c48',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const FormGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '20px',
  marginBottom: '24px',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
});

const ActionButtons = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '32px',
});

const SocialLinksContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px',
  justifyContent: 'center',
  margin: '32px 0',
});

const SocialLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: 'white',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
  },
});

const ProfilePage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    // trigger,
    reset
  } = useForm({ mode: 'onChange' });

  const [profileData, setProfileData] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [editMode, setEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Validações customizadas
  const validatePhone = (value) => {
    if (!value) return true;
    const regex = /^(\+\d{1,3})?\d{9,15}$/;
    return regex.test(value) || 'Número de telefone inválido';
  };

  const validateUrl = (value) => {
    if (!value) return true;
    const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    return regex.test(value) || 'URL inválida';
  };
  
  const validateInstagram = (value) => {
    if (!value) return true;
    const isAtFormat = value.startsWith('@');
    const isUrlFormat = /^https?:\/\/(www\.)?instagram\.com\/[A-Za-z0-9_.]+\/?$/.test(value);
    return isAtFormat || isUrlFormat || 'Deve ser @usuario ou link do Instagram';
  };

  // Função para formatar a URL da foto
  const formatPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) {
      return `${process.env.REACT_APP_API_BASE_URL}${url}`;
    }
    return null;
  };

  // Buscar dados do usuário
  const fetchUserData = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = user.token;

      if (!token) {
        throw new Error('Token não encontrado');
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userData = response.data;
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const localStoragePhoto = storedUser?.photoUrl ? formatPhotoUrl(storedUser.photoUrl) : null;
      const apiPhoto = userData.photoUrl ? formatPhotoUrl(userData.photoUrl) : null;
      const finalPhotoUrl = localStoragePhoto || apiPhoto;

      setProfileData({
        fullName: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        accountType: userData.profile?.[0] || 'Consultor',
        companyName: userData.companyName || '',
        nif: userData.nifNipc || '',
        linkedin: userData.linkedin || '',
        instagram: userData.instagram || '',
        site: userData.site || '',
        photoUrl: finalPhotoUrl,
      });

      reset({
        fullName: userData.name || '',
        phone: userData.phone || '',
        linkedin: userData.linkedin || '',
        instagram: userData.instagram || '',
        site: userData.site || '',
        companyName: userData.companyName || '',
        nif: userData.nifNipc || '',
      });
      setSelectedPhoto(finalPhotoUrl);

    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      setSnackbarMessage('Erro ao carregar perfil');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  }, [reset]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Upload de foto
  const handlePhotoUpload = async (file) => {
    if (!file) return;

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = user.token;

      if (!token) throw new Error('Token não encontrado');

      const formData = new FormData();
      formData.append('photo', file);
      const previewUrl = URL.createObjectURL(file);
      setSelectedPhoto(previewUrl);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/users/upload?email=${profileData?.email}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedUser = response.data;
      const newPhotoUrl = formatPhotoUrl(updatedUser.photoUrl);
      setProfileData(prev => ({ ...prev, photoUrl: newPhotoUrl }));

      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        photoUrl: newPhotoUrl
      }));

      setSnackbarMessage('Foto atualizada com sucesso! Favor realizar logout e login novamente.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

    } catch (error) {
      console.error('Erro ao enviar foto:', error);
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      setSelectedPhoto(storedUser?.photoUrl || profileData?.photoUrl || null);
      setSnackbarMessage('Erro ao atualizar foto');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handlePhotoUpload(file);
    }
  };

  // Enviar formulário
  const onSubmit = async (data) => {
    if (!isDirty) {
      setEditMode(false);
      return;
    }

    setIsSubmitting(true);

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const token = user.token;

      const payload = {
        name: data.fullName,
        phone: data.phone,
        linkedin: data.linkedin,
        instagram: data.instagram,
        site: data.site,
        companyName: data.companyName,
        nifNipc: data.nif,
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/users/update/${profileData.email}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const updatedUser = response.data;
      setProfileData(prev => ({
        ...prev,
        ...updatedUser,
        photoUrl: prev.photoUrl,
      }));

      const storedUser = JSON.parse(localStorage.getItem('user') || {});
      localStorage.setItem('user', JSON.stringify({
        ...storedUser,
        ...updatedUser,
        photoUrl: profileData.photoUrl,
      }));

      setSnackbarMessage('Perfil atualizado com sucesso! Favor realizar logout e login novamente.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setEditMode(false);

    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      setSnackbarMessage(error.response?.data?.message || 'Erro ao atualizar perfil');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    reset({
      fullName: profileData?.fullName || '',
      phone: profileData?.phone || '',
      linkedin: profileData?.linkedin || '',
      instagram: profileData?.instagram || '',
      site: profileData?.site || '',
      companyName: profileData?.companyName || '',
      nif: profileData?.nif || '',
    });
    setEditMode(false);
  };

  // Corrigido: useEffect que estava causando loop infinito
  useEffect(() => {
    if (editMode) {
      const subscription = watch(() => {
        // Removemos a chamada para trigger aqui para evitar o loop
      });
      return () => subscription.unsubscribe();
    }
  }, [watch, editMode]);

  if (isLoading || !profileData) {
    return (
      <FullHeightContainer maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={60} thickness={4} sx={{ color: '#151c48' }} />
        </Box>
      </FullHeightContainer>
    );
  }

  return (
    <ThemeProvider theme={profileTheme}>
      <Container>
        <ProfileHeader>
          <AvatarContainer>
            {selectedPhoto ? (
              <StyledAvatar
                src={selectedPhoto}
                alt="Foto de Perfil"
                onClick={() => fileInputRef.current.click()}
                onError={() => setSelectedPhoto(null)}
              />
            ) : (
              <StyledAvatar onClick={() => fileInputRef.current.click()}>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {profileData.fullName.charAt(0).toUpperCase()}
                </Typography>
              </StyledAvatar>
            )}
            <CameraButton onClick={() => fileInputRef.current.click()}>
              <CameraAlt sx={{ color: 'white', fontSize: '20px' }} />
            </CameraButton>
            <input
              type="file"
              ref={fileInputRef}
              hidden
              accept="image/*"
              onChange={handlePhotoChange}
            />
          </AvatarContainer>

          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5, mt: 4 }}>
            {profileData.fullName}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {profileData.accountType}
          </Typography>
        </ProfileHeader>

        <ProfileContent>
          <DarkFieldset>
            <SectionHeader>
              <SectionTitle variant="h6">
                <Edit fontSize="small" /> Informações Pessoais
              </SectionTitle>

              {editMode ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Cancelar" arrow>
                    <span> {/* Wrapper para o botão desabilitado */}
                      <IconButton onClick={handleCancelEdit} color="error" disabled={isSubmitting}>
                        <Close />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Salvar" arrow>
                    <span> {/* Wrapper para o botão desabilitado */}
                      <IconButton
                        onClick={handleSubmit(onSubmit)}
                        color="success"
                        disabled={isSubmitting || !isDirty}
                      >
                        <Check />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ) : (
                <Tooltip title="Editar perfil" arrow>
                  <IconButton onClick={() => setEditMode(true)} sx={{ color: '#151c48' }}>
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
            </SectionHeader>

            <form onSubmit={handleSubmit(onSubmit)} style={{ flex: 1 }}>
              <DarkFieldset>
                <FormControl fullWidth sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box flex={1}>
                    <FormGrid>
                      <TextField
                        label="Nome Completo"
                        variant="outlined"
                        fullWidth
                        {...register('fullName', {
                          required: 'Nome é obrigatório',
                          minLength: { value: 3, message: 'Mínimo 3 caracteres' }
                        })}
                        error={!!errors.fullName}
                        helperText={errors.fullName?.message}
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                      />

                      <TextField
                        label="Telefone"
                        variant="outlined"
                        fullWidth
                        {...register('phone', { validate: validatePhone })}
                        error={!!errors.phone}
                        helperText={errors.phone?.message}
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                        placeholder="+55 (00) 00000-0000"
                      />

                      <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        value={profileData.email}
                        disabled
                        InputLabelProps={{ shrink: true }}
                        sx={{ gridColumn: '1 / -1' }}
                      />
                    </FormGrid>

                    <Divider sx={{ my: 3 }} />

                    <SectionTitle variant="h6">
                      <Link fontSize="small" /> Redes Sociais
                    </SectionTitle>

                    <FormGrid>
                      <TextField
                        label="Instagram"
                        variant="outlined"
                        fullWidth
                        {...register('instagram', { validate: validateInstagram })}
                        error={!!errors.instagram}
                        helperText={errors.instagram?.message || 'Comece com @'}
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                        placeholder="@seuusuario"
                        InputProps={{
                          startAdornment: <Instagram sx={{ mr: 1, color: '#E1306C' }} />,
                        }}
                      />

                      <TextField
                        label="LinkedIn"
                        variant="outlined"
                        fullWidth
                        {...register('linkedin', { validate: validateUrl })}
                        error={!!errors.linkedin}
                        helperText={errors.linkedin?.message || 'URL completa'}
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                        placeholder="https://linkedin.com/in/seuperfil"
                        InputProps={{
                          startAdornment: <LinkedIn sx={{ mr: 1, color: '#0077B5' }} />,
                        }}
                      />

                      <TextField
                        label="Site Pessoal"
                        variant="outlined"
                        fullWidth
                        {...register('site', { validate: validateUrl })}
                        error={!!errors.site}
                        helperText={errors.site?.message || 'URL completa com http://'}
                        disabled={!editMode}
                        InputLabelProps={{ shrink: true }}
                        placeholder="https://seusite.com"
                        InputProps={{
                          startAdornment: <Public sx={{ mr: 1, color: '#2a3a8f' }} />,
                        }}
                        sx={{ gridColumn: '1 / -1' }}
                      />
                    </FormGrid>
                  </Box>

                  {editMode && (
                    <ActionButtons>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        disabled={isSubmitting}
                        sx={{ minWidth: '120px' }}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting || !isDirty}
                        sx={{ minWidth: '120px' }}
                      >
                        {isSubmitting ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Salvar'
                        )}
                      </Button>
                    </ActionButtons>
                  )}
                </FormControl>
              </DarkFieldset>
            </form>

            {(profileData.instagram || profileData.linkedin || profileData.site) && (
              <>
                <Divider sx={{ my: 4 }} />
                <SectionTitle variant="h6">
                  <Public fontSize="small" /> Minhas Redes
                </SectionTitle>
                <SocialLinksContainer>
                  {profileData.instagram && (
                    <Tooltip title="Instagram" arrow>
                      <SocialLink
                        href={`https://instagram.com/${profileData.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Instagram sx={{ color: '#E1306C', fontSize: '28px' }} />
                      </SocialLink>
                    </Tooltip>
                  )}
                  {profileData.linkedin && (
                    <Tooltip title="LinkedIn" arrow>
                      <SocialLink
                        href={profileData.linkedin.startsWith('http')
                          ? profileData.linkedin
                          : `https://linkedin.com/in/${profileData.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <LinkedIn sx={{ color: '#0077B5', fontSize: '28px' }} />
                      </SocialLink>
                    </Tooltip>
                  )}
                  {profileData.site && (
                    <Tooltip title="Website" arrow>
                      <SocialLink
                        href={profileData.site.startsWith('http')
                          ? profileData.site
                          : `https://${profileData.site}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Public sx={{ color: '#2a3a8f', fontSize: '28px' }} />
                      </SocialLink>
                    </Tooltip>
                  )}
                </SocialLinksContainer>
              </>
            )}
          </DarkFieldset>
        </ProfileContent>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Zoom}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setOpenSnackbar(false)}
          sx={{
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            borderRadius: '12px',
            alignItems: 'center',
            minWidth: isMobile ? '90vw' : '400px',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default ProfilePage;